/**
 * Módulo de Monitoreo Ambiental (Dashboard Weather).
 * Permite visualizar la correlación entre variables climáticas (temperatura, humedad, etc.)
 * y la incidencia de casos de geohelmintiasis en el territorio.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
    AreaChart, Area, ComposedChart, Line, Legend
} from 'recharts';
import { Apiurl } from "../../shared/services/apirest";
import MapComponent from "../ubicacion/MapComponent";
import LogoGeo from "../../assets/img/LogoGeo.png";
import LogosFooter from "../../shared/components/LogosFooter";
import "../../assets/css/DashboardWeather.css";

const DashboardWeather = () => {
    // Estado para la variable climática seleccionada y su configuración visual
    const [selectedVariable, setSelectedVariable] = useState('precipitacion');
    const [weatherMeta, setWeatherMeta] = useState({
        label: 'Precipitación (mm)',
        color: '#2196F3',
        axis_color: '#2196F3'
    });

    const [weatherCurrent, setWeatherCurrent] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [casos, setCasos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null); // Null = Mostrar todo el historial
    const [isNavCollapsed, setIsNavCollapsed] = useState(true);
    const handleNavCollapse = () => setIsNavCollapsed(!isNavCollapsed);

    /**
     * Obtiene los datos climáticos y epidemiológicos desde el servidor.
     * Realiza peticiones concurrentes para optimizar la carga.
     */
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // 1. Obtener lista de casos para el mapa
                const casosRes = await axios.get(`${Apiurl}casos/`);
                setCasos(Array.isArray(casosRes.data) ? casosRes.data : []);

                // 2. Obtener analítica climática (Gráfico bivariado y tarjetas informativas)
                const analyticsRes = await axios.get(`${Apiurl}casos/climate-analytics/?variable=${selectedVariable}`);
                const analytics = analyticsRes.data;

                setChartData(analytics.data);
                setWeatherMeta(analytics.meta);
                setWeatherCurrent(analytics.current_weather);

            } catch (err) {
                console.error("Error loading environmental data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedVariable]);

    /**
     * Filtra los casos para el mapa según la fecha seleccionada en el gráfico.
     */
    const filteredCasosMap = useMemo(() => {
        if (!selectedDate) return casos;
        return casos.filter(c => c.fecha_registro && c.fecha_registro.includes(selectedDate));
    }, [selectedDate, casos]);

    // Configuración de las variables climáticas disponibles para monitoreo
    const variables = [
        { key: 'precipitacion', label: 'Precipitación', icon: 'bi-cloud-drizzle', color: 'primary' },
        { key: 'temperatura', label: 'Temperatura', icon: 'bi-thermometer-sun', color: 'warning' },
        { key: 'humedad', label: 'Humedad', icon: 'bi-droplet-half', color: 'info' },
        { key: 'uv', label: 'Índice UV', icon: 'bi-sun', color: 'danger' },
    ];

    return (
        <div className="dw-container">
            {/* Barra de Navegación Unificada */}
            <nav className="navbar navbar-expand-lg navbar-custom navbar-dark fixed-top shadow-sm">
                <div className="container-fluid">
                    <Link className="navbar-brand" to="/">
                        <img src={LogoGeo} alt="GeoHCX" className="nav-logo-img me-2" />
                        <span className="fw-bold text-white fs-4">GEOHCX</span>
                    </Link>
                    <button
                        className="navbar-toggler"
                        type="button"
                        onClick={handleNavCollapse}
                        aria-controls="navbarNav"
                        aria-expanded={!isNavCollapsed}
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>
                    <div className={`${isNavCollapsed ? 'collapse' : ''} navbar-collapse justify-content-end`} id="navbarNav">
                        <ul className="navbar-nav align-items-center">
                            <li className="nav-item"><Link className="nav-link" to="/" onClick={() => setIsNavCollapsed(true)}>INICIO</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/quienes-somos" onClick={() => setIsNavCollapsed(true)}>QUIÉNES SOMOS</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/indicadores" onClick={() => setIsNavCollapsed(true)}>INDICADORES</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/mapa" onClick={() => setIsNavCollapsed(true)}>UBICACIÓN</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/consultar" onClick={() => setIsNavCollapsed(true)}>CONSULTAR</Link></li>
                            <li className="nav-item"><Link className="nav-link active" to="/ambiental" onClick={() => setIsNavCollapsed(true)}>DATOS AMBIENTALES</Link></li>
                            <li className="nav-item ms-lg-3">
                                <Link to="/login" className="btn-login" onClick={() => setIsNavCollapsed(true)}>INICIAR SESIÓN</Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>

            <div className="container mt-5 pt-4 pb-5">

                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="fw-bold mb-0" style={{ color: "var(--text-deep)" }}>Monitor Ambiental</h2>
                        <p className="text-muted mb-0">Correlación de variables climáticas y vigilancia epidemiológica</p>
                    </div>
                    <div className="text-end">
                        <span className="badge bg-success bg-opacity-10 text-success border border-success px-3 py-2">
                            <i className="bi bi-broadcast me-2"></i>Sincronización en tiempo real
                        </span>
                    </div>
                </div>

                {/* 1. Tarjetas de Condiciones Actuales */}
                <div className="row g-3 mb-4">
                    <div className="col-md-3">
                        <div
                            className={`dw-card p-3 d-flex align-items-center h-100 border-start border-4 border-warning cursor-pointer ${selectedVariable === 'temperatura' ? 'bg-light shadow-sm' : ''}`}
                            onClick={() => setSelectedVariable('temperatura')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="me-3 bg-warning bg-opacity-10 p-3 rounded-circle text-warning">
                                <i className="bi bi-thermometer-sun fs-2"></i>
                            </div>
                            <div>
                                <h6 className="text-muted small fw-bold mb-1">TEMPERATURA</h6>
                                <h3 className="fw-bold mb-0">{weatherCurrent?.temperature || '--'}°C</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div
                            className={`dw-card p-3 d-flex align-items-center h-100 border-start border-4 border-info cursor-pointer ${selectedVariable === 'humedad' ? 'bg-light shadow-sm' : ''}`}
                            onClick={() => setSelectedVariable('humedad')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="me-3 bg-info bg-opacity-10 p-3 rounded-circle text-info">
                                <i className="bi bi-droplet-half fs-2"></i>
                            </div>
                            <div>
                                <h6 className="text-muted small fw-bold mb-1">HUMEDAD</h6>
                                <h3 className="fw-bold mb-0">{weatherCurrent?.humidity || '--'}%</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div
                            className={`dw-card p-3 d-flex align-items-center h-100 border-start border-4 border-primary cursor-pointer ${selectedVariable === 'precipitacion' ? 'bg-light shadow-sm' : ''}`}
                            onClick={() => setSelectedVariable('precipitacion')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="me-3 bg-primary bg-opacity-10 p-3 rounded-circle text-primary">
                                <i className="bi bi-cloud-drizzle fs-2"></i>
                            </div>
                            <div>
                                <h6 className="text-muted small fw-bold mb-1">PRECIPITACIÓN</h6>
                                <h3 className="fw-bold mb-0">{weatherCurrent?.precipitation || 0} mm</h3>
                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Acumulado Diario</small>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div
                            className={`dw-card p-3 d-flex align-items-center h-100 border-start border-4 border-danger cursor-pointer ${selectedVariable === 'uv' ? 'bg-light shadow-sm' : ''}`}
                            onClick={() => setSelectedVariable('uv')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="me-3 bg-danger bg-opacity-10 p-3 rounded-circle text-danger">
                                <i className="bi bi-sun fs-2"></i>
                            </div>
                            <div>
                                <h6 className="text-muted small fw-bold mb-1">ÍNDICE UV</h6>
                                <h3 className="fw-bold mb-0">{weatherCurrent?.uv_index || 0}</h3>
                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Nivel Máximo</small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Control de Análisis Bivariado y Visualización */}
                <div className="row g-4 align-items-stretch">
                    <div className="col-lg-6">
                        <div className="dw-card p-4 h-100">
                            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
                                <h5 className="fw-bold m-0" style={{ color: 'var(--text-deep)' }}>
                                    Análisis Bivariado: {weatherMeta.label.split(' ')[0]} vs Casos
                                </h5>

                                <div className="btn-group" role="group">
                                    {variables.map(v => (
                                        <button
                                            key={v.key}
                                            type="button"
                                            className={`btn btn-sm ${selectedVariable === v.key ? 'btn-' + v.color : 'btn-outline-secondary'}`}
                                            onClick={() => setSelectedVariable(v.key)}
                                        >
                                            <i className={`bi ${v.icon} me-1`}></i>
                                            {v.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div style={{ width: '100%', height: 500 }}>
                                <ResponsiveContainer>
                                    <ComposedChart
                                        data={chartData}
                                        margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
                                        onClick={(data) => {
                                            if (data && data.activePayload) {
                                                setSelectedDate(data.activePayload[0].payload.date);
                                            }
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="dayName" fontSize={11} />

                                        {/* Eje Izquierdo: Variable Climática (Dinámico) */}
                                        <YAxis
                                            yAxisId="left"
                                            orientation="left"
                                            stroke={weatherMeta.axis_color}
                                            fontSize={11}
                                            label={{ value: weatherMeta.label, angle: -90, position: 'insideLeft', offset: 0 }}
                                        />

                                        {/* Eje Derecho: Casos (Estático) */}
                                        <YAxis
                                            yAxisId="right"
                                            orientation="right"
                                            stroke="#FF7043"
                                            fontSize={11}
                                            label={{ value: 'Casos Registrados', angle: 90, position: 'insideRight', offset: 10 }}
                                            allowDecimals={false}
                                        />

                                        <Tooltip
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                            labelFormatter={(label) => `Fecha: ${label}`}
                                        />
                                        <Legend wrapperStyle={{ paddingTop: '10px' }} />

                                        {/* Representación de la Variable Climática */}
                                        <Bar
                                            yAxisId="left"
                                            dataKey="climate_value"
                                            name={weatherMeta.label}
                                            barSize={30}
                                            fill={weatherMeta.color}
                                            radius={[4, 4, 0, 0]}
                                            cursor="pointer"
                                            animationDuration={500}
                                        />

                                        {/* Línea de Casos Registrados */}
                                        <Line
                                            yAxisId="right"
                                            type="monotone"
                                            dataKey="cases"
                                            name="Casos Registrados"
                                            stroke="#FF7043"
                                            strokeWidth={3}
                                            dot={{ r: 4, fill: '#FF7043' }}
                                            animationDuration={500}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-2 text-center small text-muted">
                                Correlación Histórica (7 días pasados) + Pronóstico Predictivo (7 días futuros)
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-6">
                        <div className="dw-card p-0 overflow-hidden h-100 d-flex flex-column">
                            <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center" style={{ height: '64px' }}>
                                <h6 className="fw-bold m-0 text-primary">
                                    <i className="bi bi-geo-alt-fill me-2"></i>
                                    Mapa Geográfico del Factor de Riesgo
                                </h6>
                                <div className="d-flex align-items-center">
                                    <span className="badge bg-white text-dark border me-2">
                                        {selectedDate ? selectedDate : "Vista General"}
                                    </span>
                                    {selectedDate && (
                                        <button
                                            className="btn btn-sm btn-outline-secondary py-0 px-2"
                                            onClick={() => setSelectedDate(null)}
                                            title="Mostrar todo"
                                        >
                                            <i className="bi bi-x"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="flex-grow-1 position-relative" style={{ height: '500px' }}>
                                <MapComponent data={filteredCasosMap} activeVariable={selectedVariable} />
                                <div className="position-absolute bottom-0 start-0 m-2 p-2 bg-white rounded shadow-sm opacity-75" style={{ zIndex: 1000, fontSize: '0.75rem' }}>
                                    <strong>{filteredCasosMap.length}</strong> Puntos de Muestreo
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Análisis Detallado de Impacto */}
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card border-0 shadow-sm p-3 bg-opacity-10" style={{ backgroundColor: `${weatherMeta.color}20` }}>
                            <div className="d-flex align-items-center">
                                <i className="bi bi-info-circle-fill fs-4 me-3" style={{ color: weatherMeta.color }}></i>
                                <div>
                                    <h6 className="fw-bold mb-1" style={{ color: weatherMeta.color }}>Análisis de Impacto: {weatherMeta.label.split(' ')[0]}</h6>
                                    <p className="small mb-0 text-muted">
                                        {selectedVariable === 'precipitacion' && "La lluvia incrementa la saturación del suelo, facilitando la supervivencia y dispersión de larvas de geohelmintos."}
                                        {selectedVariable === 'temperatura' && "Las temperaturas sostenidas por encima de 25°C optimizan el desarrollo de los huevos de parásitos en climas tropicales."}
                                        {selectedVariable === 'humedad' && "La alta humedad relativa es el factor ambiental más crítico para la viabilidad de los huevos en el ecosistema territorial."}
                                        {selectedVariable === 'uv' && "La radiación UV intensa puede actuar como un agente de control natural al degradar huevos expuestos en superficies áridas."}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* Logotipos Institucionales */}
            <LogosFooter />

            {/* Pie de Página */}
            <footer className="footer-site py-4 mt-5">
                <div className="container">
                    <div className="row align-items-center justify-content-between">
                        <div className="col-md-6 text-center text-md-start mb-3 mb-md-0">
                            <ul className="footer-nav d-flex justify-content-center justify-content-md-start mb-0" style={{ listStyle: 'none', padding: 0 }}>
                                <li className="me-4"><Link to="/" className="text-white text-decoration-none">Inicio</Link></li>
                                <li><a href="#about" className="text-white text-decoration-none">Acerca de GeoHCX</a></li>
                            </ul>
                        </div>
                        <div className="col-md-6 text-center text-md-end">
                            <p className="text-white-50 small mb-0">© 2025 GEOHCX - Monitoreo Ambiental para la Salud Pública</p>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default DashboardWeather;
